import React, { useState } from "react";
// ANT DESIGN COMPONENTS
import { Button, Collapse, InputNumber, Radio, Select, Switch, Tooltip } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
// COMPONENTS
import NodeIdEditor, { BLANK_NUMERIC_NODE_ID } from "./NodeIdEditor";
import AttributeSelect from "./AttributeSelect";
// FLATBUFFER ENUMS
import { AttributeId } from "../../../../../flatbuffers/generated/opc-ua/attribute-id";
import { MonitoringMode } from "../../../../../flatbuffers/generated/opc-ua/monitoring-mode";
import { DataChangeTrigger } from "../../../../../flatbuffers/generated/opc-ua/data-change-trigger";
import { DeadbandType } from "../../../../../flatbuffers/generated/opc-ua/deadband-type";
// INTERFACES
import type {
  OpcUaMonitoringParametersForm,
  OpcUaMonitorModeForm,
  OpcUaNodeIdForm,
  OpcUaNodeMonitorForm,
} from "../../../../../interfaces/AdapterCdoOpcUa.interface";

const { Panel } = Collapse;
const { Option } = Select;

const BLANK_PARAMS: OpcUaMonitoringParametersForm = {
  samplingIntervalMs: -1,
  queueSize: 1,
  discardOldest: true,
  monitoringMode: MonitoringMode.Reporting,
  dataChangeTrigger: DataChangeTrigger.StatusValue,
  deadbandType: DeadbandType.None_,
  deadbandValue: 0,
};

const MONITORING_MODE_OPTIONS: { value: MonitoringMode; label: string }[] = [
  { value: MonitoringMode.Disabled, label: "Disabled" },
  { value: MonitoringMode.Sampling, label: "Sampling" },
  { value: MonitoringMode.Reporting, label: "Reporting" },
];

const DATA_CHANGE_TRIGGER_OPTIONS: { value: DataChangeTrigger; label: string }[] = [
  { value: DataChangeTrigger.Status, label: "Status" },
  { value: DataChangeTrigger.StatusValue, label: "Status + Value" },
  { value: DataChangeTrigger.StatusValueTimestamp, label: "Status + Value + Timestamp" },
];

const DEADBAND_TYPE_OPTIONS: { value: DeadbandType; label: string }[] = [
  { value: DeadbandType.None_, label: "None" },
  { value: DeadbandType.Absolute, label: "Absolute" },
  { value: DeadbandType.Percent, label: "Percent" },
];

/** True when a NodeId form has a non-empty identifier (or, for a BrowsePath, at least one hop). */
function isNodeComplete(node: OpcUaNodeIdForm): boolean {
  if (node.identifierKind === "browsepath") return (node.hops ?? []).length > 0;
  return node.identifier.trim() !== "";
}

function describeNode(node: OpcUaNodeIdForm): string {
  if (node.identifierKind === "browsepath") {
    return `browsepath(${(node.hops ?? []).map((h) => h.targetName).join("/")})`;
  }
  return `ns=${node.namespaceIndex};${node.identifierKind}=${node.identifier}`;
}

function describeMode(mode: OpcUaMonitorModeForm): string {
  if (mode.kind === "poll") return `poll every ${mode.intervalMs}ms`;
  return `subscribe(${mode.subscriptionName})`;
}

/** Mode-specific fields: Subscribe references a named subscription; Poll sets a read interval. */
function MonitorModeFields({
  mode,
  onChangeMode,
}: {
  mode: OpcUaMonitorModeForm;
  onChangeMode: (mode: OpcUaMonitorModeForm) => void;
}) {
  return (
    <div className="row">
      <div className="col-12 mt-2">
        <Radio.Group
          value={mode.kind}
          onChange={(e) =>
            onChangeMode(
              e.target.value === "poll"
                ? { kind: "poll", intervalMs: 1000 }
                : { kind: "subscribe", subscriptionName: "" },
            )
          }
        >
          <Radio value="subscribe">Subscribe</Radio>
          <Radio value="poll">Poll</Radio>
        </Radio.Group>
      </div>
      {mode.kind === "subscribe" && (
        <div className="col-12 col-md-6 mt-2">
          <span className="generalStyles__inputLabel">Subscription Name</span>
          <input
            className="generalStyles__input generalStyles__width100"
            value={mode.subscriptionName}
            onChange={(e) => onChangeMode({ kind: "subscribe", subscriptionName: e.target.value })}
          />
        </div>
      )}
      {mode.kind === "poll" && (
        <div className="col-12 col-md-6 mt-2">
          <span className="generalStyles__inputLabel">Poll Interval (ms)</span>
          <InputNumber
            className="generalStyles__width100"
            size="large"
            min={0}
            value={mode.intervalMs}
            onChange={(v) => onChangeMode({ kind: "poll", intervalMs: v ?? 0 })}
          />
        </div>
      )}
    </div>
  );
}

/** Optional per-item monitoring parameters. A switch toggles between server defaults (null) and an explicit config. */
function MonitoringParamsFields({
  params,
  onChangeParams,
}: {
  params: OpcUaMonitoringParametersForm | null;
  onChangeParams: (params: OpcUaMonitoringParametersForm | null) => void;
}) {
  const onToggleEnabled = (enabled: boolean) =>
    onChangeParams(enabled ? { ...BLANK_PARAMS } : null);
  const onChangeField = <K extends keyof OpcUaMonitoringParametersForm>(
    field: K,
    value: OpcUaMonitoringParametersForm[K],
  ) => onChangeParams({ ...(params ?? BLANK_PARAMS), [field]: value });

  return (
    <div className="mt-2">
      <div className="generalStyles__flex" style={{ marginBottom: 8 }}>
        <span className="generalStyles__inputLabel">Configure monitoring parameters</span>
        <Switch checked={params !== null} onChange={onToggleEnabled} style={{ marginLeft: 8 }} />
      </div>
      {params && (
        <div className="row">
          <div className="col-12 col-md-3 mt-2">
            <span className="generalStyles__inputLabel">Sampling Interval (ms)</span>
            <InputNumber
              className="generalStyles__width100"
              size="large"
              value={params.samplingIntervalMs}
              onChange={(v) => onChangeField("samplingIntervalMs", v ?? 0)}
            />
          </div>
          <div className="col-12 col-md-3 mt-2">
            <span className="generalStyles__inputLabel">Queue Size</span>
            <InputNumber
              className="generalStyles__width100"
              size="large"
              min={0}
              value={params.queueSize}
              onChange={(v) => onChangeField("queueSize", v ?? 0)}
            />
          </div>
          <div className="col-12 col-md-3 mt-2">
            <div className="generalStyles__flex">
              <span className="generalStyles__inputLabel">Discard Oldest</span>
              <Switch
                checked={params.discardOldest}
                onChange={(checked) => onChangeField("discardOldest", checked)}
                style={{ marginLeft: 8 }}
              />
            </div>
          </div>
          <div className="col-12 col-md-3 mt-2">
            <span className="generalStyles__inputLabel">Monitoring Mode</span>
            <Select
              className="generalStyles__width100"
              size="large"
              value={params.monitoringMode}
              onChange={(v: MonitoringMode) => onChangeField("monitoringMode", v)}
            >
              {MONITORING_MODE_OPTIONS.map((opt) => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Option>
              ))}
            </Select>
          </div>
          <div className="col-12 col-md-4 mt-2">
            <span className="generalStyles__inputLabel">Data Change Trigger</span>
            <Select
              className="generalStyles__width100"
              size="large"
              value={params.dataChangeTrigger}
              onChange={(v: DataChangeTrigger) => onChangeField("dataChangeTrigger", v)}
            >
              {DATA_CHANGE_TRIGGER_OPTIONS.map((opt) => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Option>
              ))}
            </Select>
          </div>
          <div className="col-12 col-md-4 mt-2">
            <span className="generalStyles__inputLabel">Deadband Type</span>
            <Select
              className="generalStyles__width100"
              size="large"
              value={params.deadbandType}
              onChange={(v: DeadbandType) => onChangeField("deadbandType", v)}
            >
              {DEADBAND_TYPE_OPTIONS.map((opt) => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Option>
              ))}
            </Select>
          </div>
          <div className="col-12 col-md-4 mt-2">
            <span className="generalStyles__inputLabel">Deadband Value</span>
            <InputNumber
              className="generalStyles__width100"
              size="large"
              value={params.deadbandValue}
              onChange={(v) => onChangeField("deadbandValue", v ?? 0)}
            />
          </div>
        </div>
      )}
    </div>
  );
}

/** Per-node monitor configuration list: which node, observed how (Subscribe/Poll), which attribute, with which parameters. */
export default function MonitorsEditor({
  monitors,
  onChangeMonitors,
}: {
  monitors: OpcUaNodeMonitorForm[];
  onChangeMonitors: (rows: OpcUaNodeMonitorForm[]) => void;
}) {
  const [t] = useTranslation("global");
  const [draft, setDraft] = useState<OpcUaNodeMonitorForm>({
    node: { ...BLANK_NUMERIC_NODE_ID },
    mode: { kind: "subscribe", subscriptionName: "" },
    attributeId: AttributeId.Value,
    params: null,
  });

  const canAdd = isNodeComplete(draft.node);
  const onAdd = () => {
    if (!canAdd) return;
    onChangeMonitors([...monitors, draft]);
    setDraft({
      node: { ...BLANK_NUMERIC_NODE_ID },
      mode: { kind: "subscribe", subscriptionName: "" },
      attributeId: AttributeId.Value,
      params: null,
    });
  };
  const onRemove = (index: number) => onChangeMonitors(monitors.filter((_, i) => i !== index));

  return (
    <div className="container mt-3">
      <Collapse defaultActiveKey={["monitors"]}>
        <Panel header={t("general.opcUaNodeMonitors", { total: monitors.length })} key="monitors">
          {monitors.map((monitor, index) => (
            <div
              key={`${monitor.node.namespaceIndex}-${monitor.node.identifier}-${index}`}
              className="mb-3 generalStyles__flex"
              style={{ backgroundColor: "#1f1f1f", borderRadius: "5px", padding: "10px" }}
            >
              <div style={{ width: "100%" }}>
                <div className="generalStyles__textEllipsis">
                  {describeNode(monitor.node)} — {describeMode(monitor.mode)}
                </div>
              </div>
              <div className="generalStyles__flexCenter" style={{ width: "50px" }}>
                <Tooltip title="Delete">
                  <Button
                    onClick={() => onRemove(index)}
                    className="buttonStyle__29"
                    shape="circle"
                    icon={<FontAwesomeIcon icon={faTrash} />}
                  />
                </Tooltip>
              </div>
            </div>
          ))}
          <NodeIdEditor node={draft.node} onChangeNode={(node) => setDraft({ ...draft, node })} />
          <div className="row mt-2">
            <div className="col-12 col-md-6">
              <AttributeSelect
                value={draft.attributeId}
                onChange={(attributeId) => setDraft({ ...draft, attributeId })}
              />
            </div>
          </div>
          <MonitorModeFields
            mode={draft.mode}
            onChangeMode={(mode) => setDraft({ ...draft, mode })}
          />
          <MonitoringParamsFields
            params={draft.params}
            onChangeParams={(params) => setDraft({ ...draft, params })}
          />
          <div className="row mt-2">
            <div className="col-12 col-md-3">
              <Button
                onClick={onAdd}
                disabled={!canAdd}
                className={`generalStyles__width100 ${canAdd ? "buttonStyle__28" : ""}`}
              >
                <FontAwesomeIcon icon={faPlus} className="generalStyles__mrFix" />
                Add
              </Button>
            </div>
          </div>
        </Panel>
      </Collapse>
    </div>
  );
}
